import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import './Cradles.css';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSearch, faFilter, faToolbox, faPlus, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import CreateCradleModal from '../CreateCradleModal';
import EditCradleModal from '../EditCradleModal';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  const map = { pass: 'rs-badge-pass', passed: 'rs-badge-passed', fail: 'rs-badge-fail', failed: 'rs-badge-failed', pending: 'rs-badge-pending', overdue: 'rs-badge-overdue', unknown: 'rs-badge-unknown' };
  return `rs-badge ${map[key] || 'rs-badge-unknown'}`;
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try { const dt = new Date(d); return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-GB'); }
  catch { return 'N/A'; }
};

const CRADLE_TYPES = [
  'Fixed Roof-Mounted Cradle', 'Mobile Roof-Mounted Cradle', 'Ground-Based Cradle',
  'Monorail Cradle', 'Building Maintenance Unit (BMU)', 'Davits with Suspended Cradle', 'Portable/Temporary Cradle'
];

export default function Cradles({ propertyId }) {
  const [cradles, setCradles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCradle, setEditingCradle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [detailAsset, setDetailAsset] = useState(null);

  const fetchCradles = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/cradles`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to fetch cradles');

      const data = await response.json();
      setCradles((Array.isArray(data) ? data : [data]).map(c => ({
        ...c,
        lastTestDate: c.last_inspection_date,
        nextTestDate: c.next_inspection_date,
        propertyId: c.property_id,
      })));
    } catch (err) {
      console.error('Error fetching cradles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (newCradle) => {
    setCradles(prev => [...prev, {
      ...newCradle, lastTestDate: newCradle.last_inspection_date,
      nextTestDate: newCradle.next_inspection_date, propertyId: newCradle.property_id
    }]);
  };

  const handleViewTestData = (cradle) => {
    openLatestTestReport({
      propertyId,
      assetType: 'cradle',
      assetId: cradle.id,
      fallbackAsset: cradle,
      emptyMessage: 'No test data available for this cradle',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  const handleDeleteCradle = async (cradleId) => {
    if (!window.confirm('Are you sure you want to delete this cradle?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/cradles/${cradleId}`), {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete cradle');
      setCradles(prev => prev.filter(c => c.id !== cradleId));
      toast.success('Cradle deleted successfully');
    } catch (err) { toast.error('Failed to delete cradle'); }
  };

  useEffect(() => { if (propertyId) fetchCradles(); }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'cradles' && assetId && cradles.length) {
        const asset = cradles.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [cradles]);

  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) setShowFilterDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  const filtered = cradles
    .filter(c => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [c.cradle_name, c.location, c.type, c.status].some(v => v && v.toLowerCase().includes(q));
    })
    .filter(c => typeFilter === 'All' || c.type === typeFilter);

  if (error) {
    return (
      <div className="cradles-section">
        <h1>Cradles</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faToolbox} />
          <span>{error}</span>
          <button onClick={fetchCradles}>Retry</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cradles-section">
        <h1>Cradles</h1>
        <div className="rs-skeleton">{[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}</div>
      </div>
    );
  }

  return (
    <div className="cradles-section">
      <h1>Cradles</h1>

      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input type="text" placeholder="Search cradles..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      <div className="rs-table-card">
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead><tr><th>ID</th><th>Location</th><th>Type</th><th>Status</th><th>Last Test</th><th>Next Test</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7">
                  <div className="rs-empty-state">
                    <div className="rs-empty-icon"><FontAwesomeIcon icon={faToolbox} /></div>
                    <h3 className="rs-empty-title">{cradles.length === 0 ? 'No cradles yet' : 'No results found'}</h3>
                    <p className="rs-empty-desc">{cradles.length === 0 ? 'Add your first cradle to start tracking.' : 'Try adjusting your search or filter criteria.'}</p>
                  </div>
                </td></tr>
              ) : filtered.map(c => (
                <tr key={c.id}>
                  <td title={c.cradle_name}>{c.cradle_name}</td>
                  <td title={c.location}>{c.location}</td>
                  <td>{c.type}</td>
                  <td><span className={getBadgeClass(c.status)}>{c.status || 'Unknown'}</span></td>
                  <td>{formatDate(c.lastTestDate)}</td>
                  <td>{formatDate(c.nextTestDate)}</td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(c)} title="View Test Data" aria-label="View test data"><FontAwesomeIcon icon={faToolbox} /></button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(c)} title="Test History" aria-label="View test history"><FontAwesomeIcon icon={faClockRotateLeft} /></button>
                      {isSuperAdmin() && (<>
                      <button className="rs-action-btn" onClick={() => { setEditingCradle(c); setShowEditModal(true); }} title="Edit" aria-label="Edit cradle"><FontAwesomeIcon icon={faEdit} /></button>
                      <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteCradle(c.id)} title="Delete" aria-label="Delete cradle"><FontAwesomeIcon icon={faTrash} /></button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faToolbox} /></div>
              <h3 className="rs-empty-title">{cradles.length === 0 ? 'No cradles yet' : 'No results found'}</h3>
              <p className="rs-empty-desc">{cradles.length === 0 ? 'Add your first cradle.' : 'Adjust your filters.'}</p>
            </div>
          ) : filtered.map(c => (
            <div key={c.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{c.cradle_name}</span>
                <span className={getBadgeClass(c.status)}>{c.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Location</span><span className="rs-mobile-card-value">{c.location}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Type</span><span className="rs-mobile-card-value">{c.type}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Last Test</span><span className="rs-mobile-card-value">{formatDate(c.lastTestDate)}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Next Test</span><span className="rs-mobile-card-value">{formatDate(c.nextTestDate)}</span></div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(c)}><FontAwesomeIcon icon={faToolbox} /> View Report</button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(c)}><FontAwesomeIcon icon={faClockRotateLeft} /> Test History</button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingCradle(c); setShowEditModal(true); }}><FontAwesomeIcon icon={faEdit} /> Edit</button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteCradle(c.id)}><FontAwesomeIcon icon={faTrash} /> Delete</button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <CreateCradleModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={handleCreateSuccess} propertyId={propertyId} />
      {showEditModal && editingCradle && (
        <EditCradleModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} cradle={editingCradle}
          onSuccess={updated => { setCradles(prev => prev.map(c => c.id === updated.id ? { ...updated, lastTestDate: updated.last_inspection_date, nextTestDate: updated.next_inspection_date, propertyId: updated.property_id } : c)); setShowEditModal(false); }}
        />
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset ? { ...detailAsset, name: detailAsset.cradle_name, location: detailAsset.location } : null}
        assetType="cradle"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Last Test', value: formatDate(detailAsset.lastTestDate) },
          { label: 'Next Test', value: formatDate(detailAsset.nextTestDate) }
        ] : []}
      />
    </div>
  );
}
